import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 20000 });

const isDev = process.env.NODE_ENV !== "production";

/* Anonymous, first-party session tracking so we can measure UNIQUE sessions,
   pageviews and multi-service journeys in the internal digest.
   - session_id: random UUID; rolls over after 30 minutes of INACTIVITY.
   - Stored in localStorage so a page reload keeps the same session.
   - Contains NO customer personal data — a random opaque ID.
   - Consent-independent (first-party operational analytics, per UK ICO
     guidance on strictly-necessary internal measurement). */
const SESSION_TTL_MS = 30 * 60 * 1000;
const SESSION_KEY = "mp_sid";

const uuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "sid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const readSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || !s.id || !s.last) return null;
    if (Date.now() - s.last > SESSION_TTL_MS) return null;
    return s;
  } catch { return null; }
};

const writeSession = (s) => {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); }
  catch (err) { if (isDev) console.debug("[session] persist failed:", err?.message || err); }
};

export const getSessionInfo = () => {
  let s = readSession();
  const now = Date.now();
  if (!s) {
    // New session — capture entry context (referrer + UTM) on this first hit.
    const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
    s = {
      id: uuid(),
      started: now,
      last: now,
      first_path: typeof window !== "undefined" ? window.location.pathname : "",
      referrer: typeof document !== "undefined" ? (document.referrer || "").slice(0, 400) : "",
      utm_source: (params.get("utm_source") || "").slice(0, 60),
      utm_medium: (params.get("utm_medium") || "").slice(0, 60),
      utm_campaign: (params.get("utm_campaign") || "").slice(0, 60),
      utm_content: (params.get("utm_content") || "").slice(0, 60),
      is_new: true,
    };
  } else {
    s.last = now;
    s.is_new = false;
  }
  writeSession({ ...s, last: now, is_new: false });
  return s;
};

/* Privacy-conscious event tracking. Every event carries an anonymous
   session_id + first-hit context so we can compute session-level uniqueness
   in the daily digest (no persistent user identifier).
   - Backend log: always fires (first-party, anonymous).
   - GA4 mirror: only fires when window.gtag is loaded (post-consent). */
export const track = (name, properties = {}) => {
  const s = getSessionInfo();
  const props = {
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
    session_id: s.id,
    landing_page: s.first_path,
    referrer: s.referrer,
    utm_source: s.utm_source,
    utm_medium: s.utm_medium,
    utm_campaign: s.utm_campaign,
    ...properties,
  };
  if (isDev) console.debug("[track]", name, props);
  api.post("/analytics/event", { name, properties: props }).catch((err) => {
    if (isDev) console.error("[track] analytics event failed:", err?.message || err);
  });
  if (typeof window !== "undefined" && window.gtag) {
    try {
      window.gtag("event", name, isDev ? { ...props, debug_mode: true } : props);
    } catch (err) {
      if (isDev) console.error("[track] gtag event failed:", err?.message || err);
    }
  }
};

/* Route-change page_view — called by the router listener in App.js. */
export const trackPageView = (path) => {
  track("page_view", { page_path: path });
};
